import Module from "../../Module";
import { Address, Cart } from "../";
import services from "core/services";

class OrderService extends Module {
  get baseURL() {
    return "/api/v1/orders";
  }

  parseBillingMethods(methods) {
    if (!methods) return null;

    return methods.map(method => {
      return this.parseBillingMethod(method);
    });
  }

  parseBillingMethod(method) {
    return {
      id: method.id,
      title: method.title,
      active: method.active,
      description: method.description,
      methodOptions: method?.methodOptions,
      children: method.childrens,
      parentId: method.parentId,
      typeAlias: method?.typeAlias
    };
  }

  /**
   * parse array of orders
   * @param {Array} orders
   */
  parseOrders(orders) {
    if (!orders) return null;

    return orders.map(order => {
      return this.parseOrder(order);
    });
  }

  /**
   * parse object of order
   * @param {Object} order
   */
  parseOrder(order) {
    return {
      billingInfoId: order.billingInfoId,
      // cart: order.cart,
      createdAt: order.createdAt,
      customer: order.customer,
      customerId: order.customerId,
      deletedAt: order.deletedAt,
      finalPrice: order.finalPrice,
      gift: order.gift,
      giftId: order.giftId,
      id: order.id,
      shippingInfoId: order.shippingInfoId,
      status: order.status,
      steps: order.steps ? this.parseSteps(order.steps) : [],
      shippingInfo: order.shippingInfo
        ? this.parseShippingInfo(order.shippingInfo)
        : null,
      billingInfo: order.billingInfo
        ? this.parseBillingInfo(order.billingInfo)
        : null,
      cart: order.cart ? Cart.parseCart(order.cart) : [],
      storeId: order.storeId,
      title: order.title,
      trackingCode: order.trackingCode,
      updatedAt: order.updatedAt
    };
  }

  parseGateWay(gateway) {
    console.log("gateway", gateway);
    return {
      gateWayUrl: gateway?.gatewayUrl,
      token: gateway?.token
    };
  }

  parseSteps(steps) {
    return steps.map(step => {
      return this.parseStep(step);
    });
  }

  parseStep(step) {
    return {
      id: step.id,
      nextStepId: step.nextStepId,
      number: step.number,
      orderId: step.orderId,
      status: step.status
    };
  }

  parseBillingInfo(bill) {
    // console.log("billing",bill);
    return {
      createdAt: bill.createdAt,
      deletedAt: bill.deletedAt,
      description: bill.description,
      id: bill.id,
      method: bill.method ? this.parseMethod(bill.method) : null,
      methodId: bill.methodId,
      status: bill.status,
      typeAlias: bill.method?.typeAlias,
      trackingCode: bill.trackingCode,
      updatedAt: bill.updatedAt
    };
  }

  parseMethod(method) {
    return {
      createdAt: method.createdAt,
      deletedAt: method.deletedAt,
      description: method.description,
      id: method.id,
      typeAlias: method?.typeAlias,
      title: method.title,
      updatedAt: method.updatedAt
    };
  }

  parseShippingInfo(info) {
    return {
      addressId: info.addressId,
      createdAt: info.createdAt,
      deletedAt: info.deletedAt,
      description: info.description,
      id: info.id,
      method: info.method,
      methodId: info.methodId,
      myself: info.myself,
      period: info.period,
      periodId: info.periodId,
      phone: info.phone,
      recipient: info.recipient,
      updatedAt: info.updatedAt,
      price: info.price,
      deliverTimeFrom: info.deliverTimeFrom,
      deliverTimeTo: info.deliverTimeTo,
      address: info.address ? Address.parseAddress(info.address) : null
    };
  }

  parsePaymentStatus(information) {
    return {
      status: information?.status,
      state: information?.state
    };
  }

  getOrders(params) {
    return new Promise((resolve, reject) => {
      this.handler
        .get(`${this.baseURL}`, {
          params
        })
        .then(r => {
          resolve(
            this.parseResponse(r, data => ({
              orders: this.parseOrders(data.orders)
            }))
          );
        })
        .catch(e => {
          reject(this.parseResponse(e));
        });
    });
  }

  getOrder(id) {
    return new Promise((resolve, reject) => {
      this.handler
        .get(`${this.baseURL}/${id}`)
        .then(r => {
          resolve(
            this.parseResponse(r, data => ({
              order: this.parseOrder(data.order),
              price: {
                sumAfter: data.sumAfter,
                sumBefore: data.sumBefore,
                sumOfDiscounts: data.sumOfDiscounts
              }
            }))
          );
        })
        .catch(e => {
          reject(this.parseResponse(e));
        });
    });
  }

  setOrder(id) {
    return new Promise((resolve, reject) => {
      this.handler
        .post(`${this.baseURL}`)
        .then(r => {
          resolve(
            this.parseResponse(r, data => ({
              order: this.parseOrder(data.order)
            }))
          );
        })
        .catch(e => {
          reject(this.parseResponse(e));
        });
    });
  }

  getAvailableOrder(id) {
    return new Promise((resolve, reject) => {
      this.handler
        .get(`${this.baseURL}/${id}`)
        .then(r => {
          resolve(
            this.parseResponse(r, data => ({
              order: this.parseOrder(data.order),
              price: {
                sumAfter: data.sumAfter,
                sumBefore: data.sumBefore,
                sumOfDiscounts: data.sumOfDiscounts
              }
            }))
          );
        })
        .catch(e => {
          reject(this.parseResponse(e));
        });
    });
  }

  setAddress(id, model, params) {
    return new Promise((resolve, reject) => {
      this.handler
        .put(`${this.baseURL}/${id}/shippinginfo`, {
          params: params,
          data: model
        })
        .then(r => {
          resolve(
            this.parseResponse(r, data => ({
              order: this.parseOrder(data.order),
              price: {
                sumAfter: data.sumAfter,
                sumBefore: data.sumBefore,
                sumOfDiscounts: data.sumOfDiscounts
              }
            }))
          );
        })
        .catch(e => {
          reject(this.parseResponse(e));
        });
    });
  }

  getPaymentTypes() {
    return new Promise((resolve, reject) => {
      this.handler
        .get(`/api/v1/billing_method`)
        .then(r => {
          resolve(
            this.parseResponse(r, data => ({
              billingMethods: this.parseBillingMethods(data.billingMethods)
            }))
          );
        })
        .catch(e => {
          reject(this.parseResponse(e));
        });
    });
  }

  setPaymentType(id, model, params) {
    return new Promise((resolve, reject) => {
      this.handler
        .put(`${this.baseURL}/${id}/billinginfo`, {
          params: params,
          data: model
        })
        .then(r => {
          resolve(
            this.parseResponse(r, data => ({
              order: this.parseOrder(data.order),
              price: {
                sumAfter: data.sumAfter,
                sumBefore: data.sumBefore,
                sumOfDiscounts: data.sumOfDiscounts
              }
            }))
          );
        })
        .catch(e => {
          reject(this.parseResponse(e));
        });
    });
  }

  // finalize(id, params) {
  //   return new Promise((resolve, reject) => {
  //     this.handler
  //       .get(`${this.baseURL}/${id}/register`, {
  //         params: params
  //       })
  //       .then(r => {
  //         resolve(
  //           this.parseResponse(r, data => ({
  //             order: this.parseOrder(data.order)
  //           }))
  //         );
  //       })
  //       .catch(e => {
  //         reject(this.parseResponseError(e));
  //       });
  //   });
  // }

  finalize(id, model, params) {
    return new Promise((resolve, reject) => {
      this.handler
        .post(`${this.baseURL}/${id}/register`, {
          params: params,
          data: model
        })
        .then(r => {
          resolve(
            this.parseResponse(r, data => ({
              order: this.parseOrder(data.order)
            }))
          );
        })
        .catch(e => {
          reject(this.parseResponseError(e));
        });
    });
  }

  finalizeOnline(id, model, params) {
    return new Promise((resolve, reject) => {
      this.handler
        .get(`${this.baseURL}/${id}/payment`, {
          params: params,
          data: model
        })
        .then(r => {
          // console.log("req response", r);
          resolve(
            this.parseResponse(r, data => ({
              paymentGateWay: this.parseGateWay(data?.paymentGateway)
            })
            )
          );
        })
        .catch(e => {
          reject(this.parseResponseError(e));
        });
    });
  }

  //check the user payment result
  checkPaymentResult(id, params) {
    return new Promise((resolve, reject) => {
      this.handler
        .get(`${this.baseURL}/${id}`/* , {
          params
        } */)
        .then(r => {
          resolve(this.parseResponse(r, data => ({
            paymentResult: this.parsePaymentStatus(data?.order?.billingInfo)
          }))
          );
        })
        .catch(e => {
          reject(this.parseResponseError(e));
        });
    });
  }

  gift(id, model, params) {
    return new Promise((resolve, reject) => {
      this.handler
        .put(`${this.baseURL}/${id}/gift/${model}`, {
          params: params
        })
        .then(r => {
          resolve(
            this.parseResponse(r, data => ({
              order: this.parseOrder(data.order),
              price: {
                sumAfter: data.sumAfter,
                sumBefore: data.sumBefore,
                sumOfDiscounts: data.sumOfDiscounts
              }
            }))
          );
        })
        .catch(e => {
          reject(this.parseResponse(e));
        });
    });
  }
}

export default new OrderService();
